import { SET_MODEL } from '../shiftAllocation/action/ShiftAllocationAction';
import { ALLOCATION_TYPE, CLUSTER_TYPES, PROCESS_PATHS } from '../shiftAllocation/data';

const createCluster = (clusterName, clusterType, clusterProcessPathEntry) => {
  return {
    allocationType: ALLOCATION_TYPE.AISLES,
    id: `cluster_${clusterName}`,
    processPathEntries: clusterProcessPathEntry,
    title: `${clusterName}`,
    type: clusterType,
    assignments: []
  };
};

// Builds a cluster definition from aisle ranges (e.g. [{min:1,max:3},{min:4,max:10}]).
// `sortAisleReservations` is the flat, de-duped union of all aisles in those ranges; this is what
// round-trips to the backend and drives the board's aisle grouping.
const buildClusterDefinition = (identifier, isADTA, ranges, cycle, type) => {
  const sortAisleReservations = [];
  const seen = new Set();

  ranges.forEach(({ min, max }) => {
    for (let i = min; i <= max; i++) {
      if (!seen.has(i)) {
        seen.add(i);
        sortAisleReservations.push({
          identifier: `${identifier}-${i}`,
          isReservedFor: cycle,
          status: 'RES',
          type: 'AISLE'
        });
      }
    }
  });

  return { identifier, isADTA, type, sortAisleReservations };
};

const createClusterBoardEntry = (clusterName, clusterType, clusterProcessPathEntry) => {
  return {
    allocationType: ALLOCATION_TYPE.AISLES,
    id: `cluster_${clusterName}`,
    processPathEntries: clusterProcessPathEntry,
    title: `${clusterName}`,
    type: clusterType
  };
};

// Strip the "<letter>-" prefix from an aisle id like "A-12" to get the numeric aisle number.
const aisleNumberFromId = (aisle) => parseInt(String(aisle).replace(/\w-/, ''), 10);

// True if the associate can stay on a cluster whose aisles are `aisleSet`. Assignments with no
// specific aisle span are kept; an assignment is dropped only if it references an aisle no longer
// in the set.
const isAssignmentWithinAisleSet = (assignment, aisleSet) => {
  const aisles = assignment && assignment.allocationAttrs ? assignment.allocationAttrs.aisles : null;
  if (!aisles || aisles.length === 0) {
    return true;
  }
  return aisles.every((aisle) => aisleSet.has(aisleNumberFromId(aisle)));
};

export const upsertClustersAndAisles = (inputGroups, adminSettings, shiftAllocationModel, shiftAllocationDispatch) => {
  const newClusters = [];
  const newClusterNames = [];
  const newClusterDefinitions = [];
  const newBoardEntries = [];
  // clusterName -> { aisleSet, definition } for existing clusters whose range is edited.
  const updatedClusters = {};

  const boardConfig = adminSettings['laborBoard']['boardConfig'];
  const processInfo = shiftAllocationModel['processInfo'];
  const boardEntries = shiftAllocationModel['boardEntries'];
  const clusterDefinitions = shiftAllocationModel['clusterDefinitions'];

  const boardAllocationKeys = shiftAllocationModel['boardAllocationKey'].split('.');
  const cycle = boardAllocationKeys[0];

  const existingClusters = boardEntries.filter((config) => config.allocationType === ALLOCATION_TYPE.AISLES);

  // A cluster is identified on the board by its letter (id `cluster_<letter>`), independent of
  // type. Detect existing clusters by letter so editing works for ADTA and Sort alike — a
  // wrong-type edit is already blocked in the Cluster Manager before we reach this helper.
  const existingClusterLetters = new Set(existingClusters.map((cluster) => cluster.title));

  const processBoardConfigs = boardConfig.filter((config) => config.allocationType === ALLOCATION_TYPE.AISLES);

  if (processBoardConfigs && processBoardConfigs.length > 0) {
    processBoardConfigs.forEach((config) => {
      if (config.processPathEntries && config.processPathEntries.length > 0) {
        config.processPathEntries = config.processPathEntries.map((entry) => ({
          ...entry,
          minResources: 1
        }));
      }
    });

    // Group input rows by cluster letter, preserving each entered range so the board can show one
    // empty slot per group (e.g. A entered as 1-3, 4-10, 11-15, 16-22 shows those four slots).
    const rangesByCluster = {};
    for (const inputGroup of inputGroups) {
      const clusterPrefix = inputGroup['prefix'];
      const clusterName = inputGroup['cluster'];
      const minAisle = parseInt(inputGroup['minAisle'], 10);
      const maxAisle = parseInt(inputGroup['maxAisle'], 10);

      if (!clusterPrefix || !clusterName || Number.isNaN(minAisle) || Number.isNaN(maxAisle)) {
        continue;
      }
      if (!rangesByCluster[clusterName]) {
        rangesByCluster[clusterName] = { prefix: clusterPrefix, ranges: [] };
      }
      rangesByCluster[clusterName].ranges.push({ min: minAisle, max: maxAisle });
    }

    Object.keys(rangesByCluster).forEach((clusterName) => {
      const { prefix: clusterPrefix, ranges } = rangesByCluster[clusterName];
      const clusterType = CLUSTER_TYPES[clusterPrefix]['type'];
      const clusterConfig = processBoardConfigs.find((config) => config.type === clusterType);
      const clusterProcessPathEntry =
        clusterConfig && clusterConfig.processPathEntries ? clusterConfig.processPathEntries : [];
      const clusterDefinition = buildClusterDefinition(
        clusterName,
        CLUSTER_TYPES[clusterPrefix]['isADTA'],
        ranges,
        cycle,
        'CLUSTER'
      );

      if (!existingClusterLetters.has(clusterName)) {
        // New cluster: create its card + definition.
        newBoardEntries.push(createClusterBoardEntry(clusterName, clusterType, clusterProcessPathEntry));
        newClusters.push(createCluster(clusterName, clusterType, clusterProcessPathEntry));
        newClusterNames.push(clusterName);
        newClusterDefinitions.push(clusterDefinition);
      } else {
        // Existing cluster: replace its definition and prune associates no longer in the aisle set.
        const aisleSet = new Set(
          clusterDefinition.sortAisleReservations.map((aisle) => aisleNumberFromId(aisle.identifier))
        );
        updatedClusters[clusterName] = { aisleSet, definition: clusterDefinition };
      }
    });

    const updatedClusterNames = Object.keys(updatedClusters);

    // For any cluster whose range was edited, keep associates still inside the new range and move
    // those whose assigned aisle no longer exists (range shrunk) to the Bench so they aren't lost
    // and can be reassigned. Untouched clusters pass through.
    const benchedFromEdits = [];
    const prunedProcessInfo = [...processInfo, ...newClusters].map((entry) => {
      const clusterName =
        entry && typeof entry.id === 'string' && entry.id.startsWith('cluster_')
          ? entry.id.slice('cluster_'.length)
          : null;
      if (clusterName && updatedClusters[clusterName] && Array.isArray(entry.assignments)) {
        const { aisleSet } = updatedClusters[clusterName];
        const kept = [];
        entry.assignments.forEach((assignment) => {
          if (isAssignmentWithinAisleSet(assignment, aisleSet)) {
            kept.push(assignment);
          } else {
            benchedFromEdits.push({
              resourceId: assignment.resourceId,
              boardEntity: PROCESS_PATHS.BENCH,
              allocationAttrs: { process: PROCESS_PATHS.BENCH }
            });
          }
        });
        return { ...entry, assignments: kept };
      }
      return entry;
    });

    // Second pass: append the re-benched associates to the Bench (done after all clusters are
    // pruned so `benchedFromEdits` is fully populated regardless of entry ordering).
    const updatedProcessInfo = prunedProcessInfo.map((entry) =>
      entry.id === PROCESS_PATHS.BENCH && benchedFromEdits.length > 0
        ? { ...entry, assignments: [...(entry.assignments || []), ...benchedFromEdits] }
        : entry
    );

    const updatedBoardEntries = [...boardEntries, ...newBoardEntries];

    // Brand-new clusters append their definitions; edited clusters replace theirs (dropping any old
    // or duplicate definition for the same identifier) so the range truly changes instead of stacking.
    const updatedClusterDefinitions = [
      ...clusterDefinitions.filter((definition) => !updatedClusterNames.includes(definition.identifier)),
      ...newClusterDefinitions,
      ...updatedClusterNames.map((name) => updatedClusters[name].definition)
    ];

    shiftAllocationDispatch({
      type: SET_MODEL,
      payload: {
        ...shiftAllocationModel,
        processInfo: updatedProcessInfo,
        boardEntries: updatedBoardEntries,
        clusterDefinitions: updatedClusterDefinitions
      }
    });
  }
};

/**
 * Removes an entire cluster from the board: drops its card (processInfo + boardEntries) and its
 * aisle definition(s). Associates who were staffed on it are returned to the Bench so they aren't
 * lost and can be reassigned. No-op if the board entry is missing.
 */
export const removeCluster = (boardEntry, shiftAllocationModel, shiftAllocationDispatch) => {
  if (!boardEntry || !boardEntry.id) {
    return;
  }
  const clusterId = boardEntry.id;
  const clusterName = boardEntry.title;

  const clusterEntry = (shiftAllocationModel['processInfo'] || []).find((entry) => entry.id === clusterId);
  const benchedAssignments = (
    clusterEntry && Array.isArray(clusterEntry.assignments) ? clusterEntry.assignments : []
  ).map((assignment) => ({
    resourceId: assignment.resourceId,
    boardEntity: PROCESS_PATHS.BENCH,
    allocationAttrs: { process: PROCESS_PATHS.BENCH }
  }));

  const updatedProcessInfo = (shiftAllocationModel['processInfo'] || [])
    .filter((entry) => entry.id !== clusterId)
    .map((entry) => {
      if (entry.id === PROCESS_PATHS.BENCH && benchedAssignments.length > 0) {
        return { ...entry, assignments: [...(entry.assignments || []), ...benchedAssignments] };
      }
      return entry;
    });

  const updatedBoardEntries = (shiftAllocationModel['boardEntries'] || []).filter((entry) => entry.id !== clusterId);

  const updatedClusterDefinitions = (shiftAllocationModel['clusterDefinitions'] || []).filter(
    (definition) => definition.identifier !== clusterName
  );

  shiftAllocationDispatch({
    type: SET_MODEL,
    payload: {
      ...shiftAllocationModel,
      processInfo: updatedProcessInfo,
      boardEntries: updatedBoardEntries,
      clusterDefinitions: updatedClusterDefinitions
    }
  });
};
